import axios from 'axios';
import {
  Task,
  TaskComment,
  TaskAttachment,
  CreateTaskData,
  UpdateTaskData,
  CreateTaskCommentData,
  TaskFilters,
  TaskStats,
  PaginatedTasks,
  TaskApiParams,
} from '../types/task.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: `${API_URL}/tasks`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token might be expired, redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

class TaskService {
  // Helper method to transform backend task data to frontend format
  private transformTaskFromBackend(task: any): Task {
    return {
      ...task,
      // Map backend 'assigned_to' to frontend 'assignee'
      assignee: task.assigned_to || task.assignee || '',
      assigneeEmail: task.assigned_to_email || task.assigneeEmail,
      assigneeName: task.assigned_to_full_name || task.assigneeName,
      // Map other backend fields
      risk: task.risk_item || task.risk,
      riskTitle: task.risk_item_title || task.riskTitle,
      riskReference: task.risk_item_reference || task.riskReference,
      dueDate: task.due_date || task.dueDate,
      startDate: task.start_date || task.startDate,
      completedDate: task.completed_date || task.completedDate,
      estimatedHours: task.estimated_hours || task.estimatedHours,
      actualHours: task.actual_hours || task.actualHours,
      progress: task.progress_percentage ?? task.progress ?? 0,
      createdBy: task.created_by || task.createdBy,
      createdByEmail: task.created_by_email || task.createdByEmail,
      createdAt: task.created_at || task.createdAt,
      updatedAt: task.updated_at || task.updatedAt,
    };
  }

  // Task methods
  async getTasks(filters?: TaskFilters): Promise<Task[]> {
    const response = await api.get('/items/', { params: filters });
    // Handle paginated response
    if (response.data.results) {
      return response.data.results.map((task: any) => this.transformTaskFromBackend(task));
    }
    // Handle direct array response
    return response.data.map((task: any) => this.transformTaskFromBackend(task));
  }

  async getTasksPage(params: TaskApiParams): Promise<PaginatedTasks> {
    const response = await api.get('/items/', { params });
    const data = response.data;
    return {
      count: data.count ?? 0,
      next: data.next ?? null,
      previous: data.previous ?? null,
      results: (data.results ?? data).map((task: any) => this.transformTaskFromBackend(task)),
    };
  }

  async completeTask(id: string): Promise<Task> {
    const response = await api.post<any>(`/items/${id}/complete/`, {});
    return this.transformTaskFromBackend(response.data);
  }

  async reopenTask(id: string): Promise<Task> {
    const response = await api.post<any>(`/items/${id}/reopen/`);
    return this.transformTaskFromBackend(response.data);
  }

  async archiveTask(id: string): Promise<void> {
    await api.post(`/items/${id}/archive/`);
  }

  async restoreTask(id: string): Promise<void> {
    await api.post(`/items/${id}/restore/`);
  }

  async toggleChecklistItem(itemId: string): Promise<void> {
    await api.post(`/checklist/${itemId}/toggle/`);
  }

  async getTask(id: string): Promise<Task> {
    const response = await api.get<any>(`/items/${id}/`);
    return this.transformTaskFromBackend(response.data);
  }

  async createTask(data: CreateTaskData): Promise<Task> {
    // Map frontend field names to backend field names
    const backendData: any = {
      title: data.title,
      description: data.description,
      // Backend expects 'assigned_to' instead of 'assignee'
      assigned_to: data.assignee,
      status: data.status || 'TODO',
      priority: data.priority,
    };
    
    // Only include optional fields if they have values
    if (data.risk) {
      backendData.risk_item = data.risk;
    }
    if (data.dueDate) {
      backendData.due_date = data.dueDate;
    }
    if (data.startDate) {
      backendData.start_date = data.startDate;
    }
    if (data.estimatedHours !== undefined && data.estimatedHours !== null) {
      backendData.estimated_hours = data.estimatedHours;
    }
    if (data.notes !== undefined) {
      backendData.notes = data.notes;
    }
    if (data.tags !== undefined) {
      backendData.tags = data.tags;
    }
    
    const response = await api.post<any>('/items/', backendData);
    return this.transformTaskFromBackend(response.data);
  }

  async updateTask(id: string, data: UpdateTaskData): Promise<Task> {
    // Map frontend field names to backend field names
    const backendData: any = {};
    
    if (data.title !== undefined) backendData.title = data.title;
    if (data.description !== undefined) backendData.description = data.description;
    if (data.risk !== undefined) backendData.risk_item = data.risk;
    if (data.assignee !== undefined) backendData.assigned_to = data.assignee === '' ? null : data.assignee;
    if (data.status !== undefined) backendData.status = data.status;
    if (data.priority !== undefined) backendData.priority = data.priority;
    if (data.dueDate !== undefined) backendData.due_date = data.dueDate;
    if (data.startDate !== undefined) backendData.start_date = data.startDate;
    if (data.completedDate !== undefined) backendData.completed_date = data.completedDate;
    if (data.estimatedHours !== undefined) backendData.estimated_hours = data.estimatedHours;
    if (data.actualHours !== undefined) backendData.actual_hours = data.actualHours;
    if (data.progress !== undefined) backendData.progress_percentage = data.progress;
    if (data.notes !== undefined) backendData.notes = data.notes;
    if (data.tags !== undefined) backendData.tags = data.tags;
    
    const response = await api.patch<any>(`/items/${id}/`, backendData);
    return this.transformTaskFromBackend(response.data);
  }

  async deleteTask(id: string): Promise<void> {
    await api.delete(`/items/${id}/`);
  }

  async getTaskStats(filters?: Omit<TaskFilters, 'search'>): Promise<TaskStats> {
    const response = await api.get<TaskStats>('/items/stats/', { params: filters });
    return response.data;
  }

  async getMyTasks(filters?: TaskFilters): Promise<Task[]> {
    const response = await api.get<any[]>('/items/my_tasks/', { params: filters });
    return response.data.map((task: any) => this.transformTaskFromBackend(task));
  }

  async exportTasks(format: 'csv' | 'excel' | 'pdf', filters?: TaskFilters): Promise<Blob> {
    const response = await api.get('/items/export/', {
      params: { format, ...filters },
      responseType: 'blob',
    });
    return response.data;
  }

  // Comment methods
  async getTaskComments(taskId: string): Promise<TaskComment[]> {
    const response = await api.get<TaskComment[]>(`/items/${taskId}/comments/`);
    return response.data;
  }

  async addTaskComment(taskId: string, data: CreateTaskCommentData): Promise<TaskComment> {
    const response = await api.post<TaskComment>(`/items/${taskId}/comments/`, data);
    return response.data;
  }

  async deleteTaskComment(taskId: string, commentId: string): Promise<void> {
    await api.delete(`/items/${taskId}/comments/${commentId}/`);
  }

  // Attachment methods
  async getTaskAttachments(taskId: string): Promise<TaskAttachment[]> {
    const response = await api.get<TaskAttachment[]>(`/items/${taskId}/attachments/`);
    return response.data;
  }

  async uploadTaskAttachment(taskId: string, file: File): Promise<TaskAttachment> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<TaskAttachment>(
      `/items/${taskId}/attachments/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async downloadTaskAttachment(taskId: string, attachmentId: string, fileName: string): Promise<void> {
    try {
      const response = await api.get(`/items/${taskId}/attachments/${attachmentId}/download/`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      throw error;
    }
  }

  async deleteTaskAttachment(taskId: string, attachmentId: string): Promise<void> {
    await api.delete(`/items/${taskId}/attachments/${attachmentId}/`);
  }

  // Bulk operations
  async bulkUpdateTasks(
    taskIds: string[],
    data: Partial<UpdateTaskData>
  ): Promise<{ updated: number; errors: string[] }> {
    const response = await api.post('/items/bulk_update/', {
      taskIds,
      data,
    });
    return response.data;
  }

  async bulkDeleteTasks(taskIds: string[]): Promise<{ deleted: number; errors: string[] }> {
    const response = await api.post('/items/bulk_delete/', { taskIds });
    return response.data;
  }
}

const taskService = new TaskService();
export default taskService;